import { IOSConfig } from '@expo/config-plugins';
import type { JSONValue } from '@expo/json-file';
import spawnAsync from '@expo/spawn-async';
import { TarTypeFlag } from 'multitars';
import assert from 'node:assert';
import fs from 'node:fs';
import path from 'node:path';
import slugify from 'slugify';
import { Readable } from 'stream';

import { CommandError } from './errors';
import { extractStream } from './tar';

const debug = require('debug')('expo:utils:npm') as typeof console.log;

export function sanitizeNpmPackageName(name: string): string {
  // https://github.com/npm/validate-npm-package-name/#naming-rules
  return (
    applyKnownNpmPackageNameRules(name) ||
    applyKnownNpmPackageNameRules(slugify(name)) ||
    // If nothing is left use 'app' like we do in Xcode projects.
    'app'
  );
}

function applyKnownNpmPackageNameRules(name: string): string | null {
  // https://github.com/npm/validate-npm-package-name/#naming-rules

  // package name cannot start with '.' or '_'.
  while (/^(\.|_)/.test(name)) {
    name = name.substring(1);
  }

  name = name.toLowerCase().replace(/[^a-zA-Z._\-/@]/g, '');

  return (
    name
      // .replace(/![a-z0-9-._~]+/g, '')
      // Remove special characters
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') || null
  );
}

export async function npmViewAsync(...props: string[]): Promise<JSONValue> {
  const cmd = ['view', ...props, '--json'];
  const results = (await spawnAsync('npm', cmd)).stdout?.trim();
  const cmdString = `npm ${cmd.join(' ')}`;
  debug('Run:', cmdString);
  if (!results) {
    return null;
  }
  try {
    return JSON.parse(results);
  } catch (error: any) {
    throw new Error(
      `Could not parse JSON returned from "${cmdString}".\n\n${results}\n\nError: ${error.message}`
    );
  }
}

/** Given a package name like `expo` or `expo@beta`, return the registry URL if it exists. */
export async function getNpmUrlAsync(packageName: string): Promise<string> {
  const results = await npmViewAsync(packageName, 'dist');

  assert(results, `Could not get npm url for package "${packageName}"`);

  // Fully qualified url returns an object.
  // Example:
  // 𝝠 npm view expo-template-bare-minimum@sdk-33 dist --json
  if (typeof results === 'object' && !Array.isArray(results)) {
    return results.tarball as string;
  }

  // When the tag is arbitrary, the tarball is an array, return the last value as it's the most recent.
  // Example:
  // 𝝠 npm view expo-template-bare-minimum@33 dist --json
  if (Array.isArray(results)) {
    const lastResult = results[results.length - 1];

    if (lastResult && typeof lastResult === 'object' && !Array.isArray(lastResult)) {
      return lastResult.tarball as string;
    }
  }

  throw new CommandError(
    'Expected results of `npm view ...` to be an array or string. Instead found: ' + results
  );
}

export interface ExtractProps {
  expName?: string;
  filter?(path: string): boolean | undefined | null;
  strip?: number;
}

function renameNpmTarballEntries(expName: string | undefined) {
  const renameConfigs = (input: string, typeflag: TarTypeFlag): string | null => {
    if (typeflag === TarTypeFlag.FILE && path.basename(input) === 'gitignore') {
      // Rename `gitignore` because npm ignores files named `.gitignore` when publishing.
      // See: https://github.com/npm/npm/issues/1862
      return input.replace(/gitignore$/, '.gitignore');
    } else {
      return input;
    }
  };
  if (expName) {
    const androidName = IOSConfig.XcodeUtils.sanitizedName(expName.toLowerCase());
    const iosName = IOSConfig.XcodeUtils.sanitizedName(expName);
    const lowerCaseName = iosName.toLowerCase();
    return (input: string, typeflag: TarTypeFlag) => {
      input = input
        .replace(/HelloWorld/g, input.includes('android') ? androidName : iosName)
        .replace(/helloworld/g, lowerCaseName);
      return renameConfigs(input, typeflag);
    };
  } else {
    return renameConfigs;
  }
}

/**
 * Extracts a tarball stream to a directory and returns the checksum of the tarball.
 */
export async function extractNpmTarballAsync(
  stream: ReadableStream,
  output: string,
  props: ExtractProps
): Promise<string> {
  return await extractStream(stream, output, {
    filter: props.filter,
    rename: renameNpmTarballEntries(props.expName),
    strip: props.strip ?? 1,
  });
}

export async function extractNpmTarballFromUrlAsync(
  url: string,
  output: string,
  props: ExtractProps
): Promise<string> {
  const response = await fetch(url);
  if (!response.ok || !response.body) {
    throw new Error(`Unexpected response: ${response.statusText}. From url: ${url}`);
  }
  return await extractNpmTarballAsync(response.body, output, props);
}

export async function downloadAndExtractNpmModuleAsync(
  npmName: string,
  output: string,
  props: ExtractProps
): Promise<string> {
  const url = await getNpmUrlAsync(npmName);
  debug('Fetch from URL:', url);
  return await extractNpmTarballFromUrlAsync(url, output, props);
}

export async function extractLocalNpmTarballAsync(
  tarFilePath: string,
  output: string,
  props: ExtractProps
): Promise<string> {
  return await extractNpmTarballAsync(
    Readable.toWeb(fs.createReadStream(tarFilePath)) as ReadableStream,
    output,
    props
  );
}

export async function packNpmTarballAsync(packageDir: string): Promise<string> {
  const cmdArgs = ['pack', '--json', '--foreground-scripts=false'];
  const results = (
    await spawnAsync('npm', cmdArgs, {
      env: { ...process.env },
      cwd: packageDir,
    })
  ).stdout?.trim();
  try {
    const packages = normalizeNpmPackResult(JSON.parse(results));
    const packageInfo = packages?.[0];
    assert(
      packageInfo &&
        typeof packageInfo === 'object' &&
        'filename' in packageInfo &&
        typeof packageInfo.filename === 'string',
      'Expected filename property in npm pack JSON output of type "string"'
    );
    return path.resolve(packageDir, packageInfo.filename);
  } catch (error: any) {
    const cmdString = `npm ${cmdArgs.join(' ')}`;
    throw new Error(
      `Could not parse JSON returned from "${cmdString}".\n\n${results}\n\nError: ${error.message}`
    );
  }
}

/** Normalize the npm pack JSON formats used before and after npm 12 */
export function normalizeNpmPackResult(result: unknown): unknown[] | null {
  if (Array.isArray(result)) {
    return result;
  } else if (result && typeof result === 'object') {
    return Object.values(result);
  } else {
    return null;
  }
}
